<script setup>
import { usePlatformApi } from '@/utils/platformApi'
import { emailValidator, requiredValidator } from '@core/utils/validators'

definePage({ meta: { layout: 'platform', public: true } })

const router = useRouter()

const platformAccessToken = useCookie('platformAccessToken')

if (!platformAccessToken.value)
  await router.push('/platform/login')

const form = ref({
  name: '',
  slug: '',
  email: '',
  phone: '',
  address: '',
  is_active: true,
  owner_name: '',
  owner_email: '',
  owner_password: '',
  owner_password_confirmation: '',
})

const formRef = ref()
const loading = ref(false)
const errors = ref({})
const successMessage = ref('')
const isPasswordVisible = ref(false)

// Auto-generate slug dari nama tenant (developer masih bisa mengedit
// manual sebelum submit).
const slugTouched = ref(false)

watch(() => form.value.name, name => {
  if (slugTouched.value)
    return

  form.value.slug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
})

const passwordConfirmedValidator = value =>
  value === form.value.owner_password || 'Konfirmasi password tidak cocok.'

const submit = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid)
    return

  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const res = await usePlatformApi('/api/v1/platform/tenants').post({ body: form.value })
    const body = res?.data ?? res

    successMessage.value = body?.message ?? 'Tenant berhasil dibuat.'

    setTimeout(() => router.push('/platform/tenants'), 800)
  } catch (err) {
    const status = err?.status ?? err?.response?.status
    const data = err?.data ?? err?.response?._data

    if (status === 422 && data?.errors)
      errors.value = data.errors
    else
      errors.value = { _general: [data?.message ?? 'Gagal membuat tenant.'] }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="pa-6 d-flex justify-center">
    <VCard
      max-width="680"
      class="w-100"
    >
      <VCardTitle class="pt-5 px-5">
        Tambah Tenant Baru
      </VCardTitle>
      <VCardSubtitle class="px-5 pb-2">
        Role default (Owner/Manager/Cashier/Kitchen Staff) & akun Owner pertama akan otomatis
        dibuat setelah tenant tersimpan — tidak perlu tinker/database manual lagi.
      </VCardSubtitle>

      <VDivider />

      <VCardText class="pa-5">
        <VAlert
          v-if="successMessage"
          type="success"
          variant="tonal"
          class="mb-4"
        >
          {{ successMessage }}
        </VAlert>

        <VAlert
          v-if="errors._general"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ errors._general[0] }}
        </VAlert>

        <VForm
          ref="formRef"
          @submit.prevent="submit"
        >
          <p class="text-overline mb-2">
            Data Tenant
          </p>
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.name"
                label="Nama Tenant *"
                :rules="[requiredValidator]"
                :error-messages="errors.name"
              />
            </VCol>

            <VCol cols="12" md="6">
              <VTextField
                v-model="form.slug"
                label="Slug *"
                hint="Dipakai untuk subdomain/header X-Tenant, tidak bisa diubah lagi setelah dibuat"
                persistent-hint
                :rules="[requiredValidator]"
                :error-messages="errors.slug"
                @input="slugTouched = true"
              />
            </VCol>

            <VCol cols="12" md="6">
              <VTextField
                v-model="form.email"
                label="Email Tenant *"
                type="email"
                :rules="[requiredValidator, emailValidator]"
                :error-messages="errors.email"
              />
            </VCol>

            <VCol cols="12" md="6">
              <VTextField
                v-model="form.phone"
                label="Telepon"
                :error-messages="errors.phone"
              />
            </VCol>

            <VCol cols="12" md="6" class="d-flex align-center">
              <VSwitch
                v-model="form.is_active"
                label="Tenant Aktif"
                color="success"
                hide-details
              />
            </VCol>

            <VCol cols="12">
              <VTextarea
                v-model="form.address"
                label="Alamat"
                rows="2"
                :error-messages="errors.address"
              />
            </VCol>
          </VRow>

          <!--
            Sengaja TIDAK ada field logo / tax_rate / currency / timezone /
            allow_negative_stock di sini walau ada di tabel tenants. Semua
            itu didesain sebagai pengaturan self-service tenant sendiri
            (lihat halaman Settings tenant setelah login, endpoint
            /api/v1/settings & /api/v1/settings/logo) — bukan sesuatu yang
            wajar diisi platform admin di titik tenant baru dibuat (tenant
            baru bahkan belum punya logo untuk diupload). Nilai default yang
            masuk akal (tax 11%, IDR, Asia/Jakarta, dst) otomatis terisi di
            server saat tenant ini dibuat.
          -->

          <p class="text-overline mb-2 mt-2">
            Akun Owner Pertama
          </p>
          <p class="text-caption text-medium-emphasis mb-4">
            Akun ini yang dipakai pemilik tenant untuk login pertama kali. Role "Owner"
            (akses penuh) otomatis ter-assign.
          </p>
          <VRow>
            <VCol cols="12" md="6">
              <VTextField
                v-model="form.owner_name"
                label="Nama Owner *"
                :rules="[requiredValidator]"
                :error-messages="errors.owner_name"
              />
            </VCol>

            <VCol cols="12" md="6">
              <VTextField
                v-model="form.owner_email"
                label="Email Owner *"
                type="email"
                hint="Beda dari email tenant di atas juga boleh"
                persistent-hint
                :rules="[requiredValidator, emailValidator]"
                :error-messages="errors.owner_email"
              />
            </VCol>

            <VCol cols="12" md="6">
              <VTextField
                v-model="form.owner_password"
                label="Password Owner *"
                :type="isPasswordVisible ? 'text' : 'password'"
                :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                :rules="[requiredValidator]"
                :error-messages="errors.owner_password"
                @click:append-inner="isPasswordVisible = !isPasswordVisible"
              />
            </VCol>

            <VCol cols="12" md="6">
              <VTextField
                v-model="form.owner_password_confirmation"
                label="Ulangi Password Owner *"
                :type="isPasswordVisible ? 'text' : 'password'"
                :rules="[requiredValidator, passwordConfirmedValidator]"
              />
            </VCol>
          </VRow>
        </VForm>
      </VCardText>

      <VDivider />

      <VCardActions class="pa-5">
        <VSpacer />
        <VBtn
          variant="tonal"
          :to="{ path: '/platform/tenants' }"
        >
          Batal
        </VBtn>
        <VBtn
          color="primary"
          :loading="loading"
          @click="submit"
        >
          Simpan
        </VBtn>
      </VCardActions>
    </VCard>
  </div>
</template>
